<template>
    <div class="relative z-10 shrink-0 flex h-16 bg-white shadow no-print">
        <OpenMobileSidebarButton
            class="px-4 border-r border-gray-200 text-gray-500 focus:outline-none focus:bg-gray-100 focus:text-gray-600 md:hidden"
            aria-label="Open sidebar"
        >
            <svg class="h-6 w-6" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h7" />
            </svg>
        </OpenMobileSidebarButton>
        <div class="flex-1 px-4 flex justify-between">
            <div class="flex-1 flex">
                <div class="w-full flex md:ml-0" v-if="$page.props.auth.user.type === 'teacher'">
                    <label class="sr-only">
                        {{ $t('messages.search') }}
                    </label>
                    <div class="relative w-full text-gray-400 focus-within:text-gray-600">
                        <div class="absolute inset-y-0 left-0 flex items-center pointer-events-none">
                            <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                    fill-rule="evenodd"
                                    clip-rule="evenodd"
                                    d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                                />
                            </svg>
                        </div>
                        <GlobalSearchHeaderInput />
                    </div>
                </div>
                <Teleport to="#global-search">
                    <GlobalSearch ref="globalSearch"></GlobalSearch>
                </Teleport>
            </div>
            <div class="ml-4 flex items-center md:ml-6">
                <div v-if="$page.props.auth.gamificationScoreCard" class="flex gap-1">
                    <BadgePill color="green">
                        {{ $tc('messages.levels', 1) }} {{ $page.props.auth.gamificationScoreCard.current_level }}
                    </BadgePill>
                    <BadgePill color="red" class="font-mono">
                        {{ $page.props.auth.gamificationScoreCard.health_total }} /
                        {{ $page.props.auth.gamificationScoreCard.health_max }}
                    </BadgePill>
                    <BadgePill color="blue" class="font-mono">
                        {{ $page.props.auth.gamificationScoreCard.energy_total }}
                    </BadgePill>
                    <BadgePill color="yellow">
                        <AnimatedNumber
                            class="font-mono"
                            :number="$page.props.auth.gamificationScoreCard.coins_total"
                        />
                    </BadgePill>
                </div>

                <inertia-link
                    :href="route('messages.show')"
                    class="p-1 focus:outline-none focus:ring focus:text-gray-500"
                    :class="$page.props.unread_conversations_count ? 'text-indigo-400' : 'text-gray-400'"
                    aria-label="Messages"
                    v-if="$page.props.receives_messages"
                >
                    <div class="flex" v-if="$page.props.unread_conversations_count">
                        <IconMail class="animate-ping absolute w-6 h-6 text-indigo-500 mx-2.5 opacity-75" />
                        <IconMail class="relative inline-flex w-6 h-6 text-indigo-600 mx-2.5" />
                    </div>
                    <IconMail class="h-6 w-6" v-else />
                </inertia-link>

                <!--            Notification bell      -->

                <inertia-link
                    :href="route('notifications.index')"
                    class="p-1 focus:outline-none focus:ring focus:text-gray-500"
                    :class="$page.props.notification_count ? 'text-indigo-400' : 'text-gray-400'"
                    aria-label="Notifications"
                    :title="$page.props.notification_count"
                >
                    <div class="flex" v-if="$page.props.notification_count">
                        <IconBellFilled class="animate-ping absolute w-6 h-6 text-indigo-500 mx-2.5 opacity-75" />
                        <IconBellFilled class="relative inline-flex w-6 h-6 text-indigo-600 mx-2.5" />
                    </div>
                    <IconBell class="h-6 w-6" v-else />
                </inertia-link>

                <!-- Profile dropdown -->
                <div class="ml-3 relative">
                    <!--                    dropdown items-->
                    <AvatarDropdown :avatar="$page.props.auth.user.avatar_or_default">
                        <!-- Opened dropdown content -->
                        <template v-slot:content>
                            <template v-if="$page.props.auth.impersonate === true">
                                <a
                                    :href="route('leave-impersonate')"
                                    class="block px-4 py-2 text-sm text-gray-700 bg-indigo-100 hover:bg-gray-100 transition ease-in-out duration-150"
                                    role="menuitem"
                                >
                                    {{ $t('messages.leave_impersonate') }}
                                </a>
                            </template>

                            <div v-if="$page.props.auth.user.type === 'teacher'">
                                <a
                                    :href="route('teachers.show', $page.props.auth.user.id)"
                                    class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition ease-in-out duration-150"
                                    role="menuitem"
                                >
                                    {{ $t('messages.user_profile') }}
                                </a>
                                <a
                                    :href="route('user_settings.edit')"
                                    class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition ease-in-out duration-150"
                                    role="menuitem"
                                >
                                    {{ $t('messages.settings') }}
                                </a>
                                <template v-if="isAdministrator">
                                    <a
                                        :href="route('admin.dashboard.index')"
                                        class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition ease-in-out duration-150"
                                        role="menuitem"
                                        >{{ $t('messages.admin_panel') }}</a
                                    >
                                    <a
                                        v-if="$page.props.auth.features.anonymous"
                                        href="/toggle_demo_mode"
                                        class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition ease-in-out duration-150"
                                        role="menuitem"
                                    >
                                        {{ $t('messages.anonymize') }}
                                    </a>
                                </template>
                            </div>
                            <div v-if="$page.props.auth.user.type === 'janitor'">
                                <a
                                    :href="route('user_settings.edit')"
                                    class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition ease-in-out duration-150"
                                    role="menuitem"
                                >
                                    {{ $t('messages.settings') }}
                                </a>
                            </div>
                            <div v-if="$page.props.auth.user.type === 'student'">
                                <inertia-link
                                    :href="route('students.settings.edit')"
                                    class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition ease-in-out duration-150"
                                    role="menuitem"
                                >
                                    {{ $t('messages.settings') }}
                                </inertia-link>
                            </div>
                            <div v-if="$page.props.auth.user.type === 'relative'">
                                <a
                                    :href="route('families.settings.edit')"
                                    class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition ease-in-out duration-150"
                                    role="menuitem"
                                >
                                    {{ $t('messages.settings') }}
                                </a>
                            </div>
                            <a
                                :href="route('logout')"
                                onclick="event.preventDefault();document.getElementById('logout-form').submit();"
                                class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition ease-in-out duration-150"
                                role="menuitem"
                            >
                                {{ $t('messages.logout') }}
                            </a>
                            <form id="logout-form" :action="route('logout')" method="POST" style="display: none">
                                <input type="hidden" name="_token" :value="$page.props.csrf_token" />
                            </form>
                        </template>
                    </AvatarDropdown>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import BadgePill from '@/components/BadgePill.vue';
import AvatarDropdown from '@/components/AvatarDropdown.vue';
import OpenMobileSidebarButton from '@/components/OpenMobileSidebarButton.vue';
import GlobalSearchHeaderInput from '@/components/GlobalSearchHeaderInput.vue';
import { IconBell, IconBellFilled, IconMail } from '@tabler/icons-vue';
import AnimatedNumber from '../components/AnimatedNumber.vue';

export default {
    components: {
        AnimatedNumber,
        BadgePill,
        AvatarDropdown,
        OpenMobileSidebarButton,
        GlobalSearchHeaderInput,
        IconMail,
        IconBellFilled,
        IconBell,
    },
    data() {
        return {};
    },
    computed: {
        isAdministrator: function () {
            return this.$page.props.auth.user.roles.some((r) => ['administrator', 'superadmin'].includes(r));
        },
    },
};
</script>
